import { GetFileType } from '@orbit/api-types'
import React, { useEffect } from 'react'
import { downloadFile } from './fileHelpers'
import {
	ViewerBackdrop,
	ViewerButton,
	ViewerCloseButton,
	ViewerContainer,
	ViewerControls,
} from './FileViewer.styles'

interface FileViewerProps {
	/** The image file to show, or `null` when the lightbox is closed. */
	file: GetFileType | null
	onClose: () => void
	/**
	 * Notified when the blob download fails (before the open-in-new-tab
	 * fallback fires). The member page surfaces a toast here; the public page,
	 * which has no toast infra, omits it and relies on the fallback alone.
	 */
	onDownloadError?: () => void
}

/**
 * The image lightbox shared by the member and public Files pages: dark
 * backdrop, the image, a download control, and close via button / backdrop /
 * Escape. Extracted so the viewer machinery (Escape handling, robust download
 * with fallback) lives once and cannot diverge between the two surfaces.
 */
const FileViewer: React.FC<FileViewerProps> = ({ file, onClose, onDownloadError }) => {
	useEffect(() => {
		if (!file) return
		const handleEsc = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose()
		}
		document.addEventListener('keydown', handleEsc)
		return () => document.removeEventListener('keydown', handleEsc)
	}, [file, onClose])

	if (!file) return null

	return (
		<ViewerBackdrop onClick={onClose}>
			<ViewerContainer onClick={e => e.stopPropagation()}>
				<ViewerCloseButton onClick={onClose}>✕</ViewerCloseButton>
				<img src={file.url} alt={file.fileName} />
				<ViewerControls>
					<ViewerButton onClick={() => downloadFile(file, onDownloadError)}>
						↓ Download
					</ViewerButton>
				</ViewerControls>
			</ViewerContainer>
		</ViewerBackdrop>
	)
}

export default FileViewer
